<template>
  <div class="page-content">
    <div class="bg__wrapper"></div>
    <v-container class="app-container--width-max">
      <div class="content content--padding-small">
        <div class="bg-info text-white">
          <span>Success!</span>
          <p>Your application is now complete.</p>
        </div>
      </div>
    </v-container>

    <v-container class="block-info app-container--width-max">
      <div class="content content--padding-small d-flex">
        <v-card elevation="2" class="card__wrapper rounded-lg quote-content">
          <div>
            <v-alert
              color="var(--green-100)"
              class="rounded py-0 mb-6"
              :class="$vuetify.breakpoint.lgAndUp ? 'px-3' : 'pl-4 pr-0'"
              style="height: fit-content"
            >
              <div class="d-flex flex-no-wrap application-alert">
                <div class="mr-3">
                  <v-icon color="var(--green-500)" size="20"
                    >mdi-check-circle</v-icon
                  >
                </div>
                <div class="application-alert-text">
                  <span class="font-18 font-weight-bold"
                    >Application completed successfully!</span
                  >
                  <span class="font-18">
                    Please find your application reference below.</span
                  >
                </div>
              </div>
            </v-alert>
          </div>
          <p class="mb-0 font-18 black2-color">
            You can also get a quote for a different property by clicking the
            button below.
          </p>
          <div class="reference-number-container mt-5 mt-lg-9">
            <div class="reference">
              <div class="reference-number-title">Application reference</div>
              <div class="reference-number">
                {{ getApplicationReference ?? quoteReference }}
              </div>
            </div>
            <BaseButton
              depressed
              text="SUBMIT ANOTHER QUOTE"
              x-large
              rounded
              color="psorange"
              :loading="isCreatingQuote"
              loadingText="SUBMITTING..."
              height="60"
              :width="$vuetify.breakpoint.lgAndUp ? '420' : '100%'"
              className="font-weight-bold font-20 letter-spacing-04 black2-color mb-9 mb-lg-0"
              @click="
                createLeadAndLeadQuoteForMultiplePropertyLead(getQuoteRequestId)
              "
            />
          </div>

          <div class="mt-3">
            <TheSelectedQuote
              :paymentFrequency="applicationPaymentFrequency"
              :cover="getCoverLandlords"
              :insurer="insurer"
              :isPaymentStep="isPaymentStep"
              :quoteExcessConfiguration="
                getQuoteExcessConfiguration?.LandlordsInsurance
              "
              @showD2CPremiumBreakdown="showD2CPremiumBreakdown"
            />
          </div>

          <div class="guide-container">
            <h5 class="what-next-text">What happens next?</h5>
            <p class="mb-0">
              We'll send you an email with your policy information and let you
              know any details which still need to be confirmed.
            </p>
            <br />
            <p class="mb-0">
              If you need to make a claim or cancel your policy, simply follow
              the steps explained on your Insurer Schedule or contact our
              customer services team.
            </p>
            <br />
            <p class="mb-0">
              We recommend you keep all the information we've provided about
              your policy in a safe place so you can easily find all the details
              you might need if you have to contact us.
            </p>
          </div>

          <v-divider class="mt-9"></v-divider>

          <div class="document-section">
            <h5 class="font-weight-bold document-title">Your documents</h5>
            <div
              v-if="isDownloadError"
              class="col-12"
              ref="downloadError"
              :class="$vuetify.breakpoint.mdAndDown ? 'px-0' : ''"
            >
              <v-alert
                color="var(--red-100)"
                class="document-alert rounded-lg mb-0"
              >
                <div class="d-flex flex-no-wrap document-alert-content">
                  <div class="mr-3">
                    <v-icon color="var(--red-500)" size="20"
                      >mdi-alert-circle</v-icon
                    >
                  </div>
                  <div>
                    <span class="font-16 document-alert-text">
                      <strong>Sorry! </strong>The document could not be
                      downloaded at this time. Please try again.
                    </span>
                  </div>
                </div>
              </v-alert>
            </div>

            <div class="row list-document">
              <v-overlay absolute :value="isShowDocumentsOverlay">
                <v-progress-circular
                  indeterminate
                  size="64"
                ></v-progress-circular>
              </v-overlay>

              <div
                v-for="file in visibleFiles"
                :key="file.key"
                class="document-card-container col-12 col-lg-6"
              >
                <v-card
                  v-if="file.isVisible"
                  elevation="0"
                  :color="
                    file.isDownloaded ? 'bg-blue-grey-500' : 'bg-blue-grey-900'
                  "
                  :height="$vuetify.breakpoint.lgAndUp ? 360 : 340"
                  class="d-flex flex-column justify-space-between rounded-lg document-card"
                >
                  <div style="margin-bottom: 30px">
                    <div class="d-flex justify-end">
                      <div>
                        <v-img
                          v-if="!file.isDownloaded"
                          :src="require('@/assets/icons/ic_file_download.svg')"
                          transition="scale-transition"
                          :height="$vuetify.breakpoint.mdAndDown ? 32 : 33"
                          :width="$vuetify.breakpoint.mdAndDown ? 24 : 29"
                          alt="file-download"
                        />
                        <v-img
                          v-else
                          :src="
                            require('@/assets/icons/ic_file_downloaded.svg')
                          "
                          transition="scale-transition"
                          alt="file-downloaded"
                        >
                        </v-img>
                      </div>
                    </div>
                    <div class="document-card-content">
                      <h5 class="pdf-title">
                        {{ file.title }}
                      </h5>
                    </div>
                  </div>
                  <div class="d-flex justify-center">
                    <div class="download-document-button">
                      <BaseButton
                        text="DOWNLOAD"
                        height="48"
                        rounded
                        block
                        :color="
                          file.isDownloaded
                            ? 'btn-file-downloaded-text'
                            : 'psorange'
                        "
                        :class="'font-weight-bold'"
                        className="btn-file-downloaded"
                        :textColor="
                          file.isDownloaded ||
                          (file.isDownloaded && file.isDownloading)
                            ? 'white--text'
                            : ''
                        "
                        fontSize="font-20"
                        @click="downloadFile(file.key)"
                        :loading="file.isDownloading"
                        loadingText="DOWNLOADING..."
                        :loadingIconColor="
                          file.isDownloaded && file.isDownloading ? 'white' : ''
                        "
                      />
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <div
            class="question-container"
            :class="$vuetify.breakpoint.mdAndDown ? 'mt-14' : 'mt-9'"
          >
            <h5 class="question-header" style="margin-bottom: 6px">
              Questions?
            </h5>
            <div
              class="d-flex flex-column justify-space-between question-content-container"
            >
              <span class="font-16 question-content">
                If you have more questions or want to find out more information,
                you can check out our
                <a
                  title="frequently asked questions"
                  :href="FAQS_LINK"
                  target="_blank"
                  class="text-decoration-underline link-text"
                  >frequently asked questions
                  <v-icon size="10">mdi-open-in-new</v-icon></a
                >
                for more information.
              </span>
              <span class="font-16 question-content">
                Alternatively, if you require assistance you can use our
                <a
                  title="live chat"
                  target="_blank"
                  class="text-decoration-none link-text"
                  @click="openChat"
                  >live chat</a
                >
                feature or
                <a
                  title="contact us"
                  target="_blank"
                  :href="
                    isSelfServe
                      ? '/contact-us'
                      : 'https://my.paymentshield.co.uk/contact-us'
                  "
                  class="text-decoration-none link-text"
                  >contact us</a
                >.
              </span>
            </div>
          </div>
        </v-card>
      </div>
    </v-container>
    <BaseDialog
      ref="D2CDialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
      contentClass="d2c-breakdown-content"
    >
      <template v-slot:content="{ data }">
        <D2CDialogContentPremiumBreakdown
          :breakdownModel="data"
        ></D2CDialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <v-overlay :value="overlay || !getApplicationReference">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { downloadDocument } from "@/common/utils";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import TheSelectedQuote from "@/components/D2CSharedComponents/TheSelectedQuote";
import D2CDialogContentPremiumBreakdown from "@/components/QuoteResult/D2CDialogContentPremiumBreakdown";
import { FILE_KEY } from "@/constants/common.constants";
import { LANDLORD_LINK } from "@/constants/d2c-landing-page.constants";
import documentService from "@/services/document.service";
import quoteService from "@/services/quote.service";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  name: "D2CLandLordApplicationSuccessPage",
  components: {
    BaseButton,
    BaseDialog,
    TheSelectedQuote,
    D2CDialogContentPremiumBreakdown,
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      quoteDocuments: [],
      FAQS_LINK: LANDLORD_LINK.FAQS,
      isCreatingQuote: false,
      files: [
        {
          title: "Quote and Application Summary",
          key: "qas",
          isVisible: true,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Your Quote Results Summary",
          key: "multiquote",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
      ],
      isShowDocumentsOverlay: false,
      overlay: false,
      documentList: [],
      isDownloadError: false,
      isPaymentStep: false,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "getApplicationReference",
      "getAnswerFromApplyResponse",
      "quoteId",
      "selectedQuote",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "quoteReference",
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "getCoverLandlords",
      "isQuilterFactFind",
      "callChannel",
      "isSelfServe",
      "getQuoteExcessConfiguration",
    ]),
    ...mapState(APPLICATION_MODULE, ["activeQuote", "questionMap"]),
    visibleFiles() {
      return this.files.filter((f) => f.isVisible);
    },
    insurer() {
      return this.activeQuote;
    },
    applicationPaymentFrequency() {
      return this.questionMap.PaymentFrequency?.Value;
    },
  },
  async beforeMount() {
    await this.fetchQuoteExcessConfiguration();
    const paymentMethod = sessionStorage.getItem("paymentMethod");
    // this logic serves to use QuoteAndBuyBFF to notify IA backend of quote request submission
    // this is only true when ApplicationSuccessPage is in CreditCard flow and will shortly redirect to this component.
    if (this.$route.params.syncQuoteRequest) {
      this.syncQuoteRequest();
    }
    if (paymentMethod === "CreditCard") {
      await Promise.all([
        this.retrieveQuoteFollowingSuccessfulCardPayment(),
        this.repopulateActiveQuoteFromSessionStorage(),
      ]);
      sessionStorage.removeItem("paymentMethod");
    } else if (paymentMethod === "DirectDebit") {
      // remove payment method from storage so that if the page is refreshed, user is redirected to adviser hub.
      sessionStorage.removeItem("paymentMethod");
      //Params is used when retrieving a quote. If no params, redirect to adviser hub.
    }
    // get quote documents moved here to ensure state restoration when redirecting from card payment is done
    this.getQuoteDocuments();
  },
  mounted() {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });

    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(APPLICATION_MODULE, [
      "retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment",
      "repopulateActiveQuoteFromSessionStorage",
    ]),
    ...mapActions(QUESTIONS_MODULE, [
      "exitToHomePage",
      "syncQuoteRequest",
      "setRestoringLastStep",
      "fetchQuoteExcessConfiguration",
    ]),
    async createLeadAndLeadQuoteForMultiplePropertyLead(quoteRequestId) {
      if (this.isCreatingQuote) {
        return;
      }
      this.isCreatingQuote = true;
      try {
        const response =
          await quoteService.createLeadAndLeadQuoteForMultiplePropertyLead(
            quoteRequestId,
            this.httpConfig
          );
        window.onbeforeunload = null; //to remove the additional popup of "Leave Site"
        window.location.href = response.data.QuoteRequestContinuationUri;
        // return response;
      } catch (e) {
        this.isCreatingQuote = false;
        console.error(e);
      } finally {
        this.isCreatingQuote = false;
      }
    },
    async getQuoteDocuments() {
      this.isShowDocumentsOverlay = true;
      documentService
        .getQuoteDocuments(this.quoteId, this.callChannel, this.httpConfig)
        .then((res) => {
          this.quoteDocuments = res.Documents;
          this.isShowDocumentsOverlay = false;
          this.files.forEach((f) => {
            f.isVisible = res.Documents.some((doc) => doc.Code === f.key);
          });
        })
        .catch(() => {
          this.isShowDocumentsOverlay = false;
          this.isDownloadError = true;
        });
    },
    async retrieveQuoteFollowingSuccessfulCardPayment() {
      await this.retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment();
    },
    downloadFile(documentCode) {
      // `file` is the matching entry from the API response
      const file = this.quoteDocuments.find(
        (document) => document.Code === documentCode
      );

      if (!file) {
        this.isDownloadError = true;
        return;
      }

      this.isDownloadError = false;

      // `theDoc` is our local data model that holds the download status
      const theDoc = this.files.find((f) => f.key == documentCode);
      if (theDoc.isDownloading) return;
      theDoc.isDownloading = true;

      const link =
        documentCode === FILE_KEY.quoteResultsSummary
          ? this.addParamsToDocumentLink(file.Link)
          : file.Link;

      documentService
        .getDocument(link, this.httpConfig)
        .then((res) => {
          downloadDocument(res, theDoc.title);
          theDoc.isDownloading = false;
          theDoc.isDownloaded = true;
        })
        .catch((error) => {
          theDoc.isDownloading = false;
          theDoc.isDownloaded = false;
          this.isDownloadError = true;
          setTimeout(() => {
            const el = this.$refs.downloadError;
            if (el) {
              el.scrollIntoView({ behavior: "smooth" });
            }
          }, 500);
          console.error(error);
        });
    },

    /**
     * Add parameters to document download link
     * @param {string} link original document download link
     * @returns {string} Link with parameters
     */
    addParamsToDocumentLink(link) {
      try {
        const {
          BuildingsAD,
          BuildingsCoverLevel,
          ContentsAD,
          ContentsCoverLevel,
        } = this.selectedQuote?.Cover;

        return (
          link +
          `?adb=${BuildingsAD.toLowerCase()}&adc=${ContentsAD.toLowerCase()}&bcl=${BuildingsCoverLevel}&ccl=${ContentsCoverLevel}`
        );
      } catch (e) {
        console.error(e);
        return link;
      }
    },
    openChat() {
      window.Helpshift("open");
    },
  },
};
</script>

<style lang="scss" scoped>
.bg {
  &__wrapper {
    background-image: url(~@/assets/images/payment_success_background.jpg);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    position: absolute;
    top: -24px;
    left: 0px;
    height: 384px;
    width: 100%;
  }
}

:deep(.icon) {
  font-size: 20px;
  &.darkgreen-icon {
    color: #1d8649;
  }

  &.red-icon {
    color: var(--red-500);
  }

  &.blue-icon {
    color: #5285c0;
  }
}
.cover-text {
  margin-left: 3px;
  &__green {
    .cover-included {
      color: #1d8649;
    }
  }
  &__red {
    .cover-included {
      color: var(--red-500);
    }
  }
}

.block-info {
  margin-bottom: 80px;
  max-width: 1120px !important;
}

.card {
  height: 400px;
  &__wrapper {
    padding: 40px 44px 68px;
  }
}

hr {
  max-width: calc(100% + 373px);
  width: calc(100% + 126px);
  margin-left: -65px;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .reference-number-container {
    flex-direction: column;
    flex-flow: column-reverse;
  }
  .bg {
    &__wrapper {
      height: 206px;
      top: 0;
    }
  }

  .card {
    &__wrapper {
      padding: 24px 25px 43px;
    }
  }

  hr {
    max-width: calc(100% + 59px);
    width: calc(100% + 32px);
    margin-left: -16px;
  }
}

.bg-info {
  margin: 48px 48px 48px 54px;
  width: 540px !important;
  height: 140px;
  padding: 20px 5px 28px 47px;
  opacity: 0.8;
  border-radius: 4px;
  background-color: #272727;
  span {
    width: 275px;
    height: 73px;
    flex-grow: 0;
    font-size: 60px;
    font-weight: 800;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fab519;
  }

  p {
    height: 22px;
    flex-grow: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    text-align: left;
    color: #fff;
  }
}

.reference-number-title {
  font-size: 18px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.reference-number-container {
  margin: 0 4px;
  display: flex;
  justify-content: space-between;
}

.reference-number {
  margin-top: 11px;
  font-size: 30px;
  font-weight: bold;
  color: #007185;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
}

.quote {
  background-color: #354048;
}

.insurer-logo {
  border: solid 1px #7c869a;
  background-color: #fff;
}

.price {
  border: solid 1px #7c869a;
  background-color: #eff0f0;
  border-left: none;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .price {
    border-bottom-right-radius: 8px;
  }
}

.border-1 {
  border: solid 1px #7c869a;
}

.share-print {
  border: solid 1px #7c869a;
  border-left: none;
  border-bottom-right-radius: 8px;
}
@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .share-print {
    border: none;
    border-left: none;
  }
}

.btn-file-downloaded {
  border: 1px solid white;
}

.btn-file-downloaded-text {
  background-color: $blue-grey-500 !important;
}

.quote-content {
  padding: 40px 61px 36px 65px;
  margin: 0 14px;
  border-radius: 10px !important;
  background-color: #fff;
}

.application-reference-text {
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.application-alert {
  margin-top: 8px;
  margin-bottom: 5px;
}

.application-alert-text {
  color: #000;
}

.what-next-text {
  margin-bottom: 7px;
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.v-alert.document-alert {
  padding-top: 8px;
  padding-bottom: 9px;
  margin-left: 7px;
  margin-right: 6px;
  border-radius: 4px !important;
}

.document-alert-content {
  min-height: 20px;
  align-items: center;
}

.document-alert-text {
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000;
}

.list-document {
  margin: 0;
  .document-card-container {
    padding: 0;
    margin-top: 12px;
  }
  .document-card-container:nth-child(2) {
    padding-left: 18px;
    padding-right: 21px;
  }
  .document-card-container:nth-child(3) {
    padding-left: 21px;
    padding-right: 18px;
  }
}

.document-title {
  margin-bottom: 7px;
  font-family: Montserrat;
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.document-card {
  padding: 15px 26px 40px 26px;
  max-width: 440px;
}

.download-document-button {
  width: 320px;
  text-align: center;
  :deep(button.v-btn) {
    span.v-btn__content {
      div {
        div {
          font-weight: bold;
          font-stretch: normal;
          font-style: normal;
          line-height: normal;
          letter-spacing: normal;
          text-align: center;
          color: #272727;
        }
      }
    }
  }
}

.pdf-title {
  font-size: 24px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #fff;
}

.file-icon-dot {
  position: absolute;
  z-index: 1;
  font-size: 12px;
}

.document-card-content {
  margin-top: 26px;
  margin-left: 13px;
  margin-right: 26px;
  max-width: 320px;
}

.document-section {
  padding: 34.5px 0 39.5px 4px;
}

.question-container {
  margin-right: 20px;
  margin-left: 4px;
}

.question-header {
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #272727;
}

.question-content-container {
  min-height: 80px;
}

.question-content {
  margin-bottom: 0;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000;
}

.link-text {
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #007185;
}

.guide-container {
  margin-top: 31px;
  margin-left: 4px;
  margin-right: 20px;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000;
}

.application-summary-container {
  padding-left: 2px;
  padding-right: 5px;
  margin-top: -26px;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .document-card-content {
    margin-right: 15px;
  }
  .question-header {
    font-size: 18px;
  }
  .question-content-container {
    min-height: 120px;
  }
  .quote-content {
    padding: 24px 15px 40px 15px;
  }
  .document-section {
    padding: 34.5px 0 39.5px 0px;
  }
  .list-document {
    .document-card-container {
      margin-top: 12px;
      margin-bottom: 12px;
      display: flex;
      justify-content: center;
    }
    .document-card-container:nth-child(2) {
      padding-left: 15px;
      padding-right: 15px;
    }
    .document-card-container:last-child {
      padding-left: 15px;
      padding-right: 15px;
      margin-bottom: 0;
    }
    .document-card {
      max-width: 400px;
    }
  }
  .document-alert-content {
    align-items: flex-start;
  }

  .bg-info {
    padding: 9px 93px 58px 14px;
    margin: 14px 20px 23px 14px;
    margin-top: 20px;
    width: 340px !important;
    height: 130px;
    span {
      flex-grow: 0;
      font-size: 52px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: left;
      color: #fab519;
    }
  }

  .reference-number-container {
    margin: 0 2px;
  }
  .document-alert-text {
    margin-left: 4px;
    margin-right: 4px;
    border-radius: 4px !important;
  }
}

@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .download-document-button {
    width: 280px;
  }
  .block-info {
    padding-left: 2px !important;
    padding-right: 2px !important;
  }
  .document-title {
    margin: 0 2px 7px 2px;
  }
  .guide-container {
    margin-top: -5px;
    margin-left: 0;
    margin-right: 0;
  }
  .question-container {
    margin-left: 2px;
    margin-right: 2px;
  }
  .application-summary-container {
    padding-left: 0;
    padding-right: 0;
    margin-top: -28px;
  }
}

@media screen and (max-width: 420px) {
  .quote-content {
    margin: 0 0;
  }
}
</style>
